/* Right sidebar: the panel and the rail share the left sidebar's chrome surface and vocabulary. */
.container {
  background-color: var(--tuna-palette-layers);
  z-index: 1;
  transform: translateZ(0);
}

/* The panel's width animates here; the <aside> only exists while a panel is open. */
.panelFrame {
  display: flex;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  transition: width var(--tuna-transitions-duration-shorter, 200ms) var(--tuna-transitions-easing-easeInOut, ease);
}

.panel {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  min-height: 0;
  border-left: 1px solid var(--tuna-palette-divider);
}

.panelHeader {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 14px 10px 12px 18px;
  flex-shrink: 0;
}

.panelHeading {
  flex: 1;
  min-width: 0;
}

.panelTitle {
  margin: 0;
  height: 32px;
  line-height: 32px;
  font-size: var(--tuna-typography-fontSize-md);
  font-weight: 650;
  letter-spacing: -0.01em;
  color: var(--tuna-content-primary);
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.panelDescription {
  margin: 0;
  font-size: var(--tuna-typography-fontSize-xs);
  line-height: 1.45;
  color: var(--tuna-content-secondary);
}

.panelBody {
  display: flex;
  flex-direction: column;
  flex: 1;
  /* Flex items default to `min-height: auto`, which prevents shrinking below their intrinsic content
   * height, so a tall Context panel never engaged `overflow: auto`. GH #1366. */
  min-height: 0;
  overflow: auto;
  padding: 4px 10px 16px;
}

/*
 * GH #1366 (follow-up): the Context panel is a stack of Accordions with `overflow: hidden`, which makes
 * them infinitely shrinkable flex items: the column compressed them instead of scrolling. Pinning the
 * direct children to `flex-shrink: 0` keeps their natural height. Panels that manage their own scroll
 * (e.g. EntityNotes' `<VBox flex:1>`) set `flex: 1` inline, which overrides this.
 */
.panelBody > * {
  flex-shrink: 0;
}

/* Content groups inside a panel: the left sidebar's section-header style, lined up with the panel title */
.sectionHeader {
  display: flex;
  align-items: center;
  min-height: 26px;
  margin: 10px 0 2px;
  padding: 0 8px;
  color: var(--tuna-content-secondary);
  font-size: var(--tuna-typography-fontSize-xs);
  font-weight: 500;
  line-height: 1;
}

/* ---- Rail ---- */

.rail {
  display: flex;
  flex-direction: column;
  align-items: center;
  /* Top-aligned, like the left sidebar: the tools start in the same place on every screen height */
  justify-content: flex-start;
  gap: 2px;
  width: 56px;
  flex-shrink: 0;
  box-sizing: border-box;
  padding: 8px;
  border-left: 1px solid var(--tuna-palette-divider);
}

.railButton,
.closeButton {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding: 0;
  border: 0;
  border-radius: var(--tuna-shape-radius-md);
  background: transparent;
  color: var(--tuna-content-secondary);
  cursor: pointer;
}

.railButton {
  width: 40px;
  height: 40px;
}

.closeButton {
  width: 32px;
  height: 32px;
}

.railButton:hover,
.closeButton:hover {
  background-color: var(--tuna-palette-action-hover);
  color: var(--tuna-content-primary);
}

.railButton[aria-pressed='true'] {
  background-color: color-mix(in oklch, var(--tuna-palette-primary-main) 12%, transparent);
  color: var(--tuna-palette-primary-main);
}

.railButton:disabled {
  opacity: 0.38;
  cursor: default;
  pointer-events: none;
}

.railButton:focus-visible,
.closeButton:focus-visible,
.panelTitle:focus-visible {
  outline: 2px solid var(--tuna-focus-ring);
  outline-offset: -2px;
}

/* Focus lands on the title when a panel opens; only a keyboard user needs to see it there */
.panelTitle:focus:not(:focus-visible) {
  outline: none;
}

.railDivider {
  width: 24px;
  height: 1px;
  margin: 6px 0;
  flex-shrink: 0;
  background-color: var(--tuna-palette-divider);
}

@media (pointer: coarse) {
  .railButton,
  .closeButton {
    width: 44px;
    height: 44px;
  }

  .rail {
    width: 60px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .panelFrame {
    transition: none;
  }
}
